import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDensity } from '../../../hooks/useDensity';

export interface AdaptiveGridProps {
  children: ReactNode;
  minItemWidth?: number; // Minimum width in pixels for each item
  maxCols?: number; // Maximum number of columns
  className?: string;
  itemClassName?: string;
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  autoFit?: boolean; // If true, use auto-fit instead of auto-fill
  preserveAspectRatio?: boolean; // If true, maintain square aspect ratio for items
  customColumns?: string; // Custom grid-template-columns value (e.g., "45% 40% 15%")
  responsive?: {
    mobile?: number; // Max columns on mobile
    tablet?: number; // Max columns on tablet
    desktop?: number; // Max columns on desktop
  };
}

const AdaptiveGrid: React.FC<AdaptiveGridProps> = ({
  children,
  minItemWidth = 250,
  maxCols = 2,
  className = '',
  itemClassName = '',
  gap = 'md',
  autoFit: _autoFit = false,
  preserveAspectRatio = false,
  responsive,
}) => {
  const { classes, isCompact, isNormal } = useDensity();
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentCols, setCurrentCols] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Monitor container width changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      const width = container.getBoundingClientRect().width;
      setContainerWidth(width);
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);
    updateWidth(); // Initial measurement

    return () => resizeObserver.disconnect();
  }, []);

  // Get density-aware gap size
  const getGapSize = useCallback(() => {
    const gapSizes = {
      xs: isCompact ? 4 : isNormal ? 8 : 12,
      sm: isCompact ? 8 : isNormal ? 12 : 16,
      md: isCompact ? 12 : isNormal ? 16 : 24,
      lg: isCompact ? 16 : isNormal ? 24 : 32,
      xl: isCompact ? 24 : isNormal ? 32 : 48,
    };
    return gapSizes[gap];
  }, [gap, isCompact, isNormal]);

  // Calculate optimal columns based on container width and responsive breakpoints
  const calculatedColumns = useMemo(() => {
    if (!containerWidth) return 1;

    // Check responsive breakpoints
    const screenWidth = window.innerWidth;
    let responsiveMaxCols = maxCols;

    if (responsive) {
      if (screenWidth < 768 && responsive.mobile) {
        responsiveMaxCols = Math.min(responsive.mobile, maxCols);
      } else if (screenWidth < 1024 && responsive.tablet) {
        responsiveMaxCols = Math.min(responsive.tablet, maxCols);
      } else if (responsive.desktop) {
        responsiveMaxCols = Math.min(responsive.desktop, maxCols);
      }
    }

    // Calculate density-aware minimum width
    const densityAwareMinWidth = minItemWidth * (isCompact ? 0.8 : isNormal ? 1 : 1.2);

    // Calculate optimal columns based on container width
    const gapSize = getGapSize();
    const availableWidth = containerWidth - gapSize * (responsiveMaxCols - 1);
    const optimalCols = Math.floor(availableWidth / densityAwareMinWidth);

    return Math.max(1, Math.min(optimalCols, responsiveMaxCols));
  }, [containerWidth, minItemWidth, maxCols, responsive, isCompact, isNormal, getGapSize]);

  // Update current columns when calculation changes
  useEffect(() => {
    setCurrentCols(calculatedColumns);
  }, [calculatedColumns]);

  // Generate density-aware gap classes
  const gapClasses = useMemo(() => {
    const gapClassMap = {
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4',
      xl: 'gap-6',
    };
    return classes(gapClassMap[gap]);
  }, [gap, classes]);

  // Grid container classes
  const gridClasses = classes(
    [
      'grid w-full',
      gapClasses,
      // Use CSS Grid with dynamic columns
      `grid-cols-1`,
      // Responsive classes
      currentCols >= 2 ? `sm:grid-cols-2` : '',
      currentCols >= 2 ? `md:grid-cols-2` : '',
      currentCols >= 4 ? `lg:grid-cols-4` : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')
  );

  // Custom CSS for exact column control
  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: `repeat(${currentCols}, minmax(${minItemWidth}px, 1fr))`,
      ...(preserveAspectRatio && {
        gridAutoRows: `minmax(${minItemWidth}px, 1fr)`,
      }),
    }),
    [currentCols, minItemWidth, preserveAspectRatio]
  );

  // Item wrapper classes
  const itemClasses = classes(
    ['w-full', preserveAspectRatio ? 'aspect-square' : '', itemClassName].filter(Boolean).join(' ')
  );

  // Convert children to array and wrap each in item container
  const childrenArray = React.Children.toArray(children);

  return (
    <div
      ref={containerRef}
      className={gridClasses}
      style={gridStyle}
      role="grid"
      aria-label={`Adaptive grid with ${currentCols} columns`}
    >
      {childrenArray.map((child, index) => (
        <div key={index} className={itemClasses} role="gridcell">
          {child}
        </div>
      ))}
    </div>
  );
};

// Preset configurations for common use cases
export const GridPresets = {
  // Dashboard layout with 45/40/15 proportions
  dashboardLayout: {
    minItemWidth: 200,
    maxCols: 3,
    gap: 'md' as const,
    customColumns: '45% 40% 15%', // Custom proportional layout
    responsive: { mobile: 1, tablet: 2, desktop: 3 },
  },

  // Card layouts
  cardGrid: {
    minItemWidth: 280,
    maxCols: 4,
    gap: 'md' as const,
    responsive: { mobile: 1, tablet: 2, desktop: 3 },
  },

  // Dashboard widgets
  widgetGrid: {
    minItemWidth: 200,
    maxCols: 6,
    gap: 'sm' as const,
    responsive: { mobile: 1, tablet: 2, desktop: 4 },
  },

  // Vehicle inventory
  inventoryGrid: {
    minItemWidth: 300,
    maxCols: 3,
    gap: 'lg' as const,
    responsive: { mobile: 1, tablet: 2, desktop: 3 },
  },

  // Form field groups
  formGrid: {
    minItemWidth: 200,
    maxCols: 3,
    gap: 'md' as const,
    autoFit: true,
    responsive: { mobile: 1, tablet: 2, desktop: 3 },
  },

  // Compact list view
  compactGrid: {
    minItemWidth: 150,
    maxCols: 6,
    gap: 'xs' as const,
    responsive: { mobile: 2, tablet: 4, desktop: 6 },
  },
};

export default AdaptiveGrid;
